import React, { useState } from 'react';
import {
    Modal,
    Radio,
    Select,
    DatePicker,
    Button,
    Space,
    App
} from 'antd';
import {authService, salesService} from "../services/api";
import {IconExport} from "../../../components/icons/index"
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Option } = Select;

const PLATFORM_OPTIONS = [
    { value: 'pos', label: 'POS' },
    { value: 'glovo', label: 'Glovo' },
    { value: 'wolt', label: 'Wolt' },
    { value: 'yandex', label: 'Yandex' }
];

const EXPORT_TYPES = [
    { value: 'sales', label: 'Продажи' },
    { value: 'returns', label: 'Возвраты' },
    { value: 'transactions', label: 'Транзакции' }
];

const ExportModal = ({ open, onCancel }) => {
    const { message } = App.useApp();
    const [exportType, setExportType] = useState('sales');
    const [platform, setPlatform] = useState(null);
    const [dateRange, setDateRange] = useState(null);
    const [loading, setLoading] = useState(false);

    if (!authService.hasPermission('pos:sales:export')) {
        return null;
    }

    // Функция для сброса состояний формы
    const resetForm = () => {
        setExportType('sales');
        setPlatform(null);
        setDateRange(null);
    };

    const handleExport = async () => {
        if (!dateRange || dateRange.length !== 2) {
            message.error('Выберите период для экспорта');
            return;
        }

        setLoading(true);
        try {
            const params = {
                date_from: dateRange[0].format('YYYY-MM-DD'),
                date_to: dateRange[1].format('YYYY-MM-DD')
            };

            // Добавляем платформу только для продаж и возвратов
            if ((exportType === 'sales' || exportType === 'returns') && platform) {
                params.platform = platform;
            }

            let blob;
            let filename;

            switch (exportType) {
                case 'sales':
                    blob = await salesService.exportSalesCSV(params);
                    filename = `sales_${params.date_from}_${params.date_to}.csv`;
                    break;
                case 'returns':
                    blob = await salesService.exportReturnsCSV(params);
                    filename = `returns_${params.date_from}_${params.date_to}.csv`;
                    break;
                case 'transactions':
                    blob = await salesService.exportTransactionsCSV(params);
                    filename = `transactions_${params.date_from}_${params.date_to}.csv`;
                    break;
                default:
                    throw new Error('Неизвестный тип экспорта');
            }

            if (blob.result === 'error') {
                message.error(blob.message);
                return;
            }

            // Создаем ссылку для скачивания файла
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            message.success('Файл успешно экспортирован');

            // Сбрасываем состояния формы после успешного экспорта
            resetForm();
            onCancel();
        } catch (error) {
            message.error(error.message || 'Ошибка при экспорте данных');
        } finally {
            setLoading(false);
        }
    };

    // Обработчик отмены с сбросом формы
    const handleCancel = () => {
        resetForm();
        onCancel();
    };

    const showPlatformFilter = exportType === 'sales' || exportType === 'returns';

    return (
        <Modal
            title="Экспорт данных"
            open={open}
            onCancel={handleCancel}
            footer={
                <Space>
                    <Button onClick={handleCancel}>
                        Отмена
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleExport}
                        loading={loading}
                        icon={<IconExport/>}
                    >
                        Экспортировать в CSV
                    </Button>
                </Space>
            }
            width={500}
        >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div>
                    <div style={{ marginBottom: 8 }}>Тип данных для экспорта:</div>
                    <Radio.Group
                        value={exportType}
                        onChange={(e) => setExportType(e.target.value)}
                    >
                        {EXPORT_TYPES.map(type => (
                            <Radio key={type.value} value={type.value}>
                                {type.label}
                            </Radio>
                        ))}
                    </Radio.Group>
                </div>

                {showPlatformFilter && (
                    <div>
                        <div style={{ marginBottom: 8 }}>Платформа:</div>
                        <Select
                            placeholder="Все платформы"
                            allowClear
                            style={{ width: '100%' }}
                            value={platform}
                            onChange={setPlatform}
                        >
                            {PLATFORM_OPTIONS.map(option => (
                                <Option key={option.value} value={option.value}>
                                    {option.label}
                                </Option>
                            ))}
                        </Select>
                    </div>
                )}

                <div>
                    <div style={{ marginBottom: 8 }}>Период:</div>
                    <RangePicker
                        value={dateRange}
                        onChange={setDateRange}
                        format="DD.MM.YYYY"
                        style={{ width: '100%' }}
                        placeholder={['Дата с', 'Дата по']}
                        disabledDate={(current, { from }) => {
                            // Блокируем будущие даты
                            if (current && current > dayjs().endOf('day')) {
                                return true;
                            }

                            // Если выбрана начальная дата, ограничиваем конечную дату
                            if (from && current) {
                                const diffDays = current.diff(from, 'day');
                                return diffDays > 30; // 31 день включительно (0-30)
                            }

                            return false;
                        }}
                    />
                </div>
            </Space>
        </Modal>
    );
};

export default ExportModal;